import { Navigate, useLocation } from 'react-router-dom';
import { useAuthStore } from '../store/authStore';
import { useState, useEffect } from 'react';

interface AdminProtectedRouteProps {
  children: React.ReactNode;
}

/**
 * Protected route specifically for Admin routes.
 * Reads from both zustand and localStorage for reliability.
 */
export const AdminProtectedRoute = ({ children }: AdminProtectedRouteProps) => {
  const { isAuthenticated, user, token } = useAuthStore();
  const location = useLocation();
  const [isReady, setIsReady] = useState(false);
  const [authData, setAuthData] = useState<{ user: any; token: string } | null>(null);

  // Read from localStorage on mount
  useEffect(() => {
    try {
      const stored = localStorage.getItem('auth-storage');
      console.log('AdminProtectedRoute: Reading localStorage:', stored ? 'found' : 'not found');
      
      if (stored) {
        const parsed = JSON.parse(stored);
        const state = parsed?.state;
        if (state?.token && state?.user) {
          console.log('AdminProtectedRoute: Found valid auth in localStorage:', {
            role: state.user.role,
            email: state.user.email
          });
          setAuthData({ user: state.user, token: state.token });
        }
      }
    } catch (e) {
      console.error('AdminProtectedRoute: Error reading localStorage:', e);
    }
    setIsReady(true);
  }, []);

  // Wait for initial check
  if (!isReady) {
    return (
      <div className="flex items-center justify-center h-screen">
        <div className="text-lg">جاري التحميل...</div>
      </div>
    );
  }

  // Use zustand state if available, otherwise use localStorage data
  const effectiveUser = user || authData?.user;
  const effectiveToken = token || authData?.token;
  const effectiveIsAuthenticated = isAuthenticated || !!(authData?.user && authData?.token);

  console.log('AdminProtectedRoute - checking auth:', {
    zustandAuth: isAuthenticated,
    localStorageAuth: !!(authData?.user && authData?.token),
    effectiveAuth: effectiveIsAuthenticated,
    role: effectiveUser?.role,
    pathname: location.pathname
  });

  // If not authenticated, redirect to admin login
  if (!effectiveIsAuthenticated || !effectiveUser || !effectiveToken) {
    console.log('AdminProtectedRoute - not authenticated, redirecting to /admin/login');
    return <Navigate to="/admin/login" replace state={{ from: location.pathname }} />;
  }

  // Check if user has admin role
  const userRole = String(effectiveUser.role || '').trim();
  const isAdminRole = userRole === 'Admin' || userRole === 'SuperAdmin' || userRole === '3' || userRole === '4';
  
  if (!isAdminRole) {
    console.log('AdminProtectedRoute - not admin, redirecting to /', { role: userRole });
    return <Navigate to="/" replace />;
  }

  console.log('AdminProtectedRoute - admin authenticated, allowing access');
  return <>{children}</>;
};
